/**
 * What a form field looks like — defined once.
 *
 * The four controls that sit next to each other in every form used to
 * disagree about nearly everything (two corner radii, three focus treatments,
 * one field with no fill at all). Every field wears this now: Input, Textarea,
 * SelectTrigger, DatePicker, and the native select on touch.
 *
 * Two things in it are load-bearing:
 *
 * `border-border-strong`, not `border-input`. `--input` is a near-white FILL
 * within 1.01:1 of both --card and --background, so the fill does nothing to
 * distinguish a field from the page — the outline is the entire affordance,
 * and as `border-input` it measured 1.02:1. WCAG 1.4.11 asks 3:1 of the part
 * of a control that identifies it; --border-strong is tuned to 3.35:1.
 *
 * `text-control`: 15px under a mouse and 16px under a finger (--fs-control).
 * Below 16px iOS Safari zooms the viewport the instant a field takes focus and
 * never zooms back, which the app used to suppress with `maximum-scale=1` — at
 * the cost of pinch-zoom for everyone (WCAG 1.4.4). Sizing the control fixes
 * the cause.
 *
 * `focus-visible`, not `focus`, everywhere — Select rang on a mouse click,
 * which reads as a stuck state.
 *
 * Sizing is NOT here: a textarea is tall and a select is not. This is the
 * surface — fill, border, radius, type, focus — and every field wears it.
 */
export const FIELD_CHROME =
  "w-full rounded border border-border-strong bg-input px-3 py-2 " +
  "text-control text-foreground placeholder:text-muted-foreground " +
  "disabled:cursor-not-allowed disabled:opacity-50 " +
  "transition-[box-shadow,border-color] duration-fast ease-standard " +
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring";

/**
 * The standard single-line field height: 40px under a pointer.
 *
 * `touch:h-auto` releases the fixed height so `min-h-touch` can take it to 44pt
 * under a finger — a fixed height would win over a min-height and leave a 40px
 * target. Select used `touch:h-11`, which lands on the same 44px by a different
 * route; one spelling now.
 */
export const FIELD_HEIGHT = "h-10 touch:h-auto touch:min-h-touch";
